import type { Option } from "@tento-lona/sheets";
import { Tracing } from "@tento-core/tracing";
import { LiveData } from "@tento-core/livedata";
import type { DateFragment, DateTime, Duration, Utc } from "@tento-chrono";
import type { EventCategory } from "@tento-lona";
import type { Movable } from "./interactable-item.ts";
import type { EventGhost } from "./event-ghost.ts";

/** Per-`CalendarDaysStrip` repaint callback. Each days-row filters its
 *  own visible columns via `overlapsExclusive`, so the service fans
 *  out to ALL registered renderers and only the overlapping row
 *  actually paints.
 *
 *  `settle` is only meaningful when `ghost` is null (a clear): `"commit"` ⇒
 *  the drag persisted, so the renderer commits the dropped position as an
 *  optimistic bridge until the `canonicalCells` write repaints; `"revert"` ⇒
 *  the drag was CANCELLED with no write, so the renderer must repaint the
 *  ORIGINAL position (nothing will reconcile a committed bridge). */
export type PendingGhostRenderer = (
  ghost: Option<EventGhost>,
  settle: PendingGhostRenderer.Settle,
) => void;

/** Types that go with `PendingGhostRenderer`: `Settle`. */
export namespace PendingGhostRenderer {
  /** How a cleared ghost settles: the dropped position is kept, or the original repainted. */
  export type Settle = "commit" | "revert";
}

/** Whether a column pointer event (a time-of-day offset within
 *  `windowed`'s day) lands on the ghost's own span. Surface gestures
 *  (Dismiss) use this to tell "click on the drafted pending event"
 *  apart from "click on empty column": the former is part of the
 *  pending interaction, not a dismissal of it. Exclusive end — a
 *  click at the span's bottom edge is outside it. */
export function ghostContainsPointerEvent(
  ghost: EventGhost,
  windowed: DateFragment.Windowed,
  position: Duration.Time,
): boolean {
  const clicked = windowed.fragment.start
    .withTime(position.toTimeOfDayWrapping())
    .toUtc();
  return ghost.newTimeRange.containsExclusiveEnd(clicked);
}

/**
 * Layout-neutral drag-ghost service: the single in-flight ghost for
 * the timed grid — an existing item mid-drag/resize (any `Movable`)
 * or a new-item create range. Extracted from `CalendarActions` so the
 * painter is a standalone service every renderer reaches through its
 * `RendererGestureHost` rather than through the events actions class.
 *
 * One ghost at a time by design: gestures are mutually exclusive on
 * the column manager, so the last writer wins and `clear()` is the
 * idempotent backstop after every settle.
 */
export class DragGhostService {
  readonly #_current: LiveData<Option<EventGhost>> =
    LiveData.new<Option<EventGhost>>(null);
  readonly #renderers = new Set<PendingGhostRenderer>();

  /** Readable handle for consumers that bind reactively. */
  get current(): LiveData.Readable<Option<EventGhost>> {
    return this.#_current;
  }

  /**
   * Register a repaint callback (one per days-row instance — visible
   * plus buffered weeks). Replays the current ghost so a freshly
   * bound renderer paints any in-flight drag. Returns the disposer.
   */
  addRenderer(renderer: PendingGhostRenderer): () => void {
    this.#renderers.add(renderer);
    Tracing.log("drag-ghost/register-renderer", { total: this.#renderers.size });
    renderer(this.#_current.get(), "commit");
    return () => {
      this.#renderers.delete(renderer);
    };
  }

  /** Paint the in-flight ghost for an existing item mid-drag/resize. */
  update(item: Movable, newTimeRange: DateTime.Range<Utc>): void {
    this.#set({ type: "existing", item, newTimeRange }, "commit");
  }

  /** Paint the new-item create ghost. */
  showNew(
    timeRange: DateTime.Range<Utc>,
    title: Option<string>,
    category: Option<EventCategory>,
    backgroundColor: Option<string>,
  ): void {
    this.#set(
      { type: "new", title, category, backgroundColor, newTimeRange: timeRange },
      "commit",
    );
  }

  /** Clear the ghost after a PERSISTED drag. Idempotent. Renderers commit the
   *  dropped position as an optimistic bridge until the `canonicalCells` write
   *  repaints. Use {@link revert} when the drag was cancelled. */
  clear(): void {
    this.#set(null, "commit");
  }

  /** Clear the ghost after a CANCELLED drag (no write). Renderers repaint the
   *  ORIGINAL position rather than committing the dropped one — without this the
   *  bridge would strand the item at the dropped spot, since no `canonicalCells`
   *  write follows to reconcile it. Idempotent. */
  revert(): void {
    this.#set(null, "revert");
  }

  #set(ghost: Option<EventGhost>, settle: PendingGhostRenderer.Settle): void {
    this.#_current.update(ghost);
    Tracing.log("drag-ghost/set", {
      ghostType: ghost?.type ?? null,
      settle,
      rendererCount: this.#renderers.size,
    });
    for (const renderer of this.#renderers) {
      renderer(ghost, settle);
    }
  }
}
